import { useEffect, useMemo, useState } from "react";
import Heading from "@/components/tpr/Heading/Heading";
import { PreferredLocationsSelector } from "@/components/tpr/PreferredLocationsSelector/PreferredLocationsSelector";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import { BreadcrumbItem, BreadcrumbLink } from "@/components/ui/breadcrumb";
import { Button } from "@/components/ui/button";
import { Item, ItemActions, ItemContent, ItemDescription, ItemTitle } from "@/components/ui/item";
import { Select, SelectContent, SelectGroup, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Slider } from "@/components/ui/slider";
import { Spinner } from "@/components/ui/spinner";
import { Switch } from "@/components/ui/switch";
import { defaultButtonClasses, defaultItemClasses } from "@/constants/ui";
import { useBreadcrumb } from "@/contexts/breadcrumbContext";
import { useMember } from "@/contexts/memberContext";
import { useFont } from "@/contexts/fontContext";
import { gaPageView } from "@/core/google";
import { useApiVersionQuery } from "@/hooks/useApiVersionQuery";
import { usePermissibleLocationsQuery } from "@/hooks/usePermissibleLocationsQuery";
import { tprStorage } from "@/core/storage";
import { cn, getInitials } from "@/lib/utils";
import { MemberService } from "@/services/MemberService";
import { OrganisationService } from "@/services/OrganisationService";
import { Crown } from "lucide-react";
import type { OrganisationMember } from "@/api/organisationApi";
import type { Location } from "@/types/Location";
import { BodyFontOptions, type BodyFont } from "@/types/MemberSettings";

const SettingsView = () => {
  const { member, refreshMember } = useMember();
  const { bodyFont, setBodyFont: setGlobalBodyFont } = useFont();
  const { data: apiVersion, isLoading: isLoadingApiVersion } = useApiVersionQuery();
  const memberService = new MemberService();
  const organisationService = new OrganisationService();
  const [discoModeEnabled, setDiscoModeEnabled] = useState(member?.settings?.discoModeEnabled ?? false);
  // Theme and animations are no longer member-toggleable — assume light mode + animations on.
  const theme = "light" as const;
  const allowAnimation = true;
  const [calendarWidth, setCalendarWidth] = useState(member?.settings?.calendarWidth ?? 500);
  const [calendarType, setCalendarType] = useState<"day" | "week">(
    member?.settings?.calendarTypePreference ?? "day",
  );
  const [preferredLocationIds, setPreferredLocationIds] = useState<string[]>(member?.preferredLocations ?? []);
  const [saveStatus, setSaveStatus] = useState<"idle" | "saving" | "success" | "error">("idle");
  const [saveError, setSaveError] = useState<string | null>(null);

  // Organisation owner state
  const [isOwner, setIsOwner] = useState(false);
  const [organisationMembers, setOrganisationMembers] = useState<OrganisationMember[]>([]);
  const [isLoadingOrgMembers, setIsLoadingOrgMembers] = useState(false);
  const [organisation, setOrganisation] = useState<{ id: string; name: string; avatar?: string; address?: string }>({
    id: "",
    name: "",
  });

  // Preferred-locations options: all locations for elevated users, the member's accessible
  // locations otherwise. getPermissableLocations (via this hook) already encodes that rule.
  const { data: availableLocations = [] } = usePermissibleLocationsQuery();

  const selectedLocations = useMemo(() => {
    if (!preferredLocationIds.length || !availableLocations.length) {
      return [];
    }
    // Maintain order of preferredLocationIds
    return preferredLocationIds
      .map((id) => availableLocations.find((loc) => loc.id === id))
      .filter((loc): loc is Location => loc !== undefined);
  }, [preferredLocationIds, availableLocations]);

  useEffect(() => {
    gaPageView("/settings");
  }, []);

  // Fetch organisation members if the user owns an organisation
  useEffect(() => {
    const fetchOrgMembers = async () => {
      // if (!member?.ownerOfOrganisationId) {
      //   return;
      // }

      setIsLoadingOrgMembers(true);
      try {
        if (member?.organisationId) {
          const response = await organisationService.checkIsOwnerAndGetMembers(member?.organisationId || "");
          if (response?.isOwner && response.members) {
            setIsOwner(true);
            setOrganisationMembers(response.members);
            setOrganisation(response.organisation);
          }
        }
      } catch (error) {
        console.error("Failed to fetch organisation members:", error);
      } finally {
        setIsLoadingOrgMembers(false);
      }
    };

    fetchOrgMembers();
  }, [member?.ownerOfOrganisationId]);

  const handleSaveSettings = async (e?: React.MouseEvent<HTMLButtonElement>) => {
    e?.preventDefault();
    e?.stopPropagation();

    console.log("Save settings clicked");

    if (!member?.id) {
      console.error("No member ID found");
      setSaveError("Member ID not found");
      setSaveStatus("error");
      return;
    }

    console.log("Saving settings for member:", member.id);
    setSaveStatus("saving");
    setSaveError(null);

    try {
      const settingsToSave = {
        theme,
        calendarTypePreference: calendarType,
        discoModeEnabled,
        calendarWidth,
        allowAnimation,
        bodyFont,
      };
      console.log("Settings to save:", settingsToSave);

      const updateData = {
        settings: settingsToSave,
        preferredLocations: preferredLocationIds,
      };

      const result = await memberService.updateMember(member.id, updateData);

      if (result instanceof Error) {
        console.error("Update returned error:", result);
        throw result;
      }

      console.log("Settings update successful:", result);

      // Update local storage with new settings
      const updatedMember = {
        ...member,
        settings: {
          theme,
          calendarTypePreference: calendarType,
          discoModeEnabled,
          calendarWidth,
          allowAnimation,
          bodyFont,
        },
        preferredLocations: preferredLocationIds,
      };

      tprStorage.set("tpruser", updatedMember);
      console.log("Updated local storage");

      // Refresh the member context so other components see the updated settings immediately
      // This ensures the calendar and other views reflect the new settings without requiring a page reload
      await refreshMember();

      setSaveStatus("success");

      // Auto-clear success message after 4 seconds
      setTimeout(() => {
        setSaveStatus("idle");
      }, 4000);
    } catch (error) {
      console.error("Failed to save settings:", error);
      setSaveStatus("error");
      setSaveError(error instanceof Error ? error.message : "Failed to save settings");

      // Auto-clear error after 4 seconds
      setTimeout(() => {
        setSaveStatus("idle");
        setSaveError(null);
      }, 4000);
    }
  };

  const { setBreadcrumb } = useBreadcrumb();

  useEffect(() => {
    setBreadcrumb(
      <>
        <BreadcrumbItem className="text-sm text-gray-600 truncate max-w-[125px] md:max-w-none">
          <BreadcrumbLink href="#">Settings</BreadcrumbLink>
        </BreadcrumbItem>
      </>,
    );
    return () => setBreadcrumb(null);
  }, [setBreadcrumb]);

  return (
    <div>
      <div className="px-4 min-h-[80vh]">
        <div className="mb-8 mt-8">
          <Heading title={`Settings`} />
        </div>
        <div className="flex flex-col gap-2">
          <Item variant="outline" className={defaultItemClasses}>
            <ItemContent>
              <div className="flex flex-col">
                <ItemTitle className="block text-base md:text-1xl font-semibold">Calendar view</ItemTitle>
                <ItemDescription>Which calendar type suits your needs: day or week.</ItemDescription>
              </div>
            </ItemContent>
            <ItemActions>
              <Select
                value={calendarType}
                onValueChange={(value) => {
                  setCalendarType(value as "day" | "week");
                }}
              >
                <SelectTrigger className="bg-white text-black dark:bg-gray-800 dark:text-white justify-center items-center text-lg [&_span]:text-black [&_span]:dark:white [&_span]:truncate [&_span]:pr-4">
                  <SelectValue placeholder={calendarType} />
                </SelectTrigger>
                <SelectContent>
                  <SelectGroup>
                    <SelectItem value="day">Day</SelectItem>
                    <SelectItem value="week">Week</SelectItem>
                    {/* <SelectItem value="month">Month</SelectItem> */}
                  </SelectGroup>
                </SelectContent>
              </Select>
            </ItemActions>
          </Item>

          <Item variant="outline" className={defaultItemClasses}>
            <ItemContent>
              <div className="flex flex-col">
                <ItemTitle className="block text-base md:text-1xl font-semibold">Font</ItemTitle>
                <ItemDescription>Choose your preferred font for body text throughout the application.</ItemDescription>
              </div>
            </ItemContent>
            <ItemActions>
              <Select
                value={bodyFont}
                onValueChange={(value) => {
                  setGlobalBodyFont(value as BodyFont);
                }}
              >
                <SelectTrigger className="bg-white text-black dark:bg-gray-800 dark:text-white justify-center items-center text-lg [&_span]:text-black [&_span]:dark:white [&_span]:truncate [&_span]:pr-4">
                  <SelectValue placeholder={bodyFont} />
                </SelectTrigger>
                <SelectContent>
                  <SelectGroup>
                    {BodyFontOptions.map((font) => (
                      <SelectItem key={font} value={font}>
                        <span style={{ fontFamily: font === "EB Garamond" ? '"EB Garamond", serif' : `"${font}", sans-serif` }}>
                          {font}
                        </span>
                      </SelectItem>
                    ))}
                  </SelectGroup>
                </SelectContent>
              </Select>
            </ItemActions>
          </Item>

          <Item variant="outline" className={defaultItemClasses}>
            <ItemContent>
              <div className="flex flex-col">
                <ItemTitle className="block text-base md:text-1xl font-semibold">
                  Calendar width
                </ItemTitle>
                <ItemDescription>How wide do you wish for each day to be within your calendar?</ItemDescription>
              </div>
            </ItemContent>
            <ItemActions>
              <div className="flex min-w-[100px] md:min-w-[150px] lg:min-w-[200px]">
                <Slider
                  defaultValue={[calendarWidth]}
                  min={300}
                  max={1000}
                  step={10}
                  onValueChange={(value) => setCalendarWidth(value[0])}
                />
              </div>
              <span>{calendarWidth}px</span>
            </ItemActions>
          </Item>
          <Item variant="outline" className={defaultItemClasses}>
            <ItemContent>
              <div className="flex flex-col">
                <ItemTitle className="block text-base md:text-1xl font-semibold">Disco mode</ItemTitle>
                <ItemDescription>Do you wish for rooms on your calendar to be coloured?</ItemDescription>
              </div>
            </ItemContent>
            <ItemActions>
              <div className="flex items-center space-x-2">
                <Switch id="disco-mode" checked={discoModeEnabled} onCheckedChange={setDiscoModeEnabled} />
              </div>
            </ItemActions>
          </Item>

          {availableLocations.length > 0 && (
            <Item variant="outline" className={defaultItemClasses}>
              <ItemContent>
                <div className="flex flex-col w-full">
                  <ItemTitle className="block text-base md:text-1xl font-semibold">Preferred Locations</ItemTitle>
                  <ItemDescription className="mb-4">
                    Select and order your preferred locations for quicker access when booking (maximum of 5)
                  </ItemDescription>
                  <PreferredLocationsSelector
                    selectedLocations={selectedLocations}
                    availableLocations={availableLocations}
                    onChange={setPreferredLocationIds}
                    maxItems={5}
                  />
                </div>
              </ItemContent>
            </Item>
          )}

          {isOwner && (
            <Item variant="outline" className={defaultItemClasses}>
              <ItemContent>
                <div className="flex flex-col">
                  <ItemTitle className="block text-base md:text-1xl font-semibold">Organisation Members</ItemTitle>
                  <ItemDescription>
                    You are the owner of {organisation.name || "your organisation"}. You cannot manage organisation
                    members here, but you can see which members are within your organisation. Contact Head Office if you
                    wish to make changes.
                  </ItemDescription>
                  <div className="space-y-2 mt-4">
                    {isLoadingOrgMembers ? (
                      <div className="text-center py-4">
                        <p className="text-gray-500 text-sm">Loading organisation members...</p>
                      </div>
                    ) : organisationMembers.length > 0 ? (
                      <div className="max-h-[300px] overflow-y-auto space-y-2 border rounded-md p-2 bg-gray-50">
                        {organisationMembers.map((orgMember) => {
                          const isMemberOwner = orgMember.id === member?.id;
                          return (
                            <div key={orgMember.id}>
                              <div className="flex items-center space-x-3">
                                <Avatar className="w-10 h-10">
                                  <AvatarImage src={orgMember.avatar} alt="Member Avatar" />
                                  <AvatarFallback>
                                    {getInitials(`${orgMember.firstName} ${orgMember.lastName}`)}
                                  </AvatarFallback>
                                </Avatar>
                                <div className="flex-1">
                                  <div className="flex items-center gap-2">
                                    <span className="font-semibold text-gray-900">
                                      {orgMember.firstName} {orgMember.lastName}
                                    </span>
                                    {isMemberOwner && (
                                      <span className="inline-flex items-center gap-1 text-xs font-medium text-amber-700 bg-amber-100 px-2 py-0.5 rounded-full">
                                        <Crown className="w-3 h-3" />
                                        Owner
                                      </span>
                                    )}
                                  </div>
                                  <p className="text-sm text-gray-600">{orgMember.email}</p>
                                </div>
                              </div>
                            </div>
                          );
                        })}
                      </div>
                    ) : (
                      <div className="text-center py-8 border rounded-md bg-gray-50">
                        <p className="text-gray-500 text-sm">No organisation members found</p>
                      </div>
                    )}
                  </div>
                </div>
              </ItemContent>
            </Item>
          )}

          <Item variant="outline" className={defaultItemClasses}>
            <ItemContent>
              <div className="flex flex-col">
                <ItemTitle className="block text-base md:text-1xl font-semibold">Version</ItemTitle>
                <ItemDescription>Client & API</ItemDescription>
              </div>
            </ItemContent>
            <ItemActions>
              <div className="flex flex-col text-right items-end">
                <div>Client <span className="text-muted-foreground">{import.meta.env.VITE_COMMIT_HASH || "n/a"} &middot; {import.meta.env.VITE_APP_ENV || "n/a"}</span></div>
                <div>API <span className="text-muted-foreground">
                  {isLoadingApiVersion ? "..." : (apiVersion?.version?.substring(0, 8) || "n/a")} &middot; {apiVersion?.environment || "n/a"}
                </span></div>
              </div>
            </ItemActions>
          </Item>

          <div className="flex flex-col">
            <Button
              type="submit"
              onClick={handleSaveSettings}
              disabled={saveStatus === "saving"}
              size="lg"
              className={saveStatus === "success" ? "bg-green-600 hover:bg-green-700" : cn(defaultButtonClasses, "self-end mt-4 mb-4 lg:w-auto")}
            >
              {saveStatus === "saving" ? (
                <>
                  <Spinner className="size-4 mr-2" />
                  Saving...
                </>
              ) : saveStatus === "success" ? (
                <>
                  <svg
                    className="size-4 mr-2"
                    fill="none"
                    stroke="currentColor"
                    viewBox="0 0 24 24"
                    xmlns="http://www.w3.org/2000/svg"
                  >
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                  </svg>
                  Saved!
                </>
              ) : (
                "Save Changes"
              )}
            </Button>
            {saveStatus === "success" && (
              <div className="text-green-600 dark:text-green-400 bg-green-100 dark:bg-green-900/30 h-10 px-3 rounded-md flex-1 flex items-center">
                Settings saved successfully.
              </div>
            )}
            {saveStatus === "error" && (
              <div className="text-red-600 dark:text-red-400 bg-red-100 dark:bg-red-900/30 h-9 px-3 rounded-md flex-1 flex items-center">
                Sorry, we can't save that right now. Error: {saveError}
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default SettingsView;
